/**
 * The 26 GI Engine segments.
 *
 * Source of truth: docs/SEGMENT-MAP.md. Each segment has:
 *
 *   - `id`            — kebab-case slug; URL-safe; matches SEGMENT-MAP route
 *   - `tier`          — 1 (adapter exists), 2 (adapter planned), 3 (vision)
 *   - `status`        — `ready` only when an adapter crate exists in
 *                       `crates/gi-adapter-*`. Otherwise `coming-soon`.
 *   - `engineDomain`  — the closest of the 7 engine `DomainType` enums.
 *                       Multiple segments can map to the same domain
 *                       (e.g. music + photography both → engine layer
 *                       work, but one is Audio and one is Generic).
 *   - `adapterCrate`  — `gi-adapter-<name>` string when ready; null otherwise.
 *   - `Icon`          — lucide-react component (capitalised import).
 *   - `connectors`    — planned external integrations from SEGMENT-MAP.
 *                       All ship with `status: "planned"` for now.
 *   - `starters`      — exactly 5 starter recipes per segment (Tier 3
 *                       has 3 in the spec; we emit those 3 and leave
 *                       2 placeholders so UI rendering stays uniform).
 *
 * Human-readable copy (segment names, descriptions, starter names,
 * starter one-liners) lives in `i18n/locales/{en,he}/segments.json`.
 * Keep this file structural only — every label is a translation key.
 */

import {
  Banknote,
  Bot,
  BrainCircuit,
  Camera,
  Car,
  Clapperboard,
  Cpu,
  Factory,
  Gamepad2,
  Globe2,
  GraduationCap,
  HardHat,
  Heart,
  Home,
  Hourglass,
  Landmark,
  Leaf,
  Music,
  Scale,
  Shield,
  ShieldHalf,
  ShoppingCart,
  Truck,
  Umbrella,
  Users,
  Video,
  type LucideIcon,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────────

export type SegmentTier = 1 | 2 | 3;
export type SegmentStatus = 'ready' | 'coming-soon';
export type ConnectorStatus = 'available' | 'planned';

/** Engine `DomainType` string variants, mirrored from
 *  `src/features/spark/schemas.ts` / `gi-types::DomainType`. */
export type EngineDomain =
  | 'Audio'
  | 'Process'
  | 'Medical'
  | 'Financial'
  | 'Defense'
  | 'Government'
  | 'Generic';

export interface SegmentConnector {
  /** Stable id used for i18n lookup: `connectors.<id>`. ASCII only. */
  id: string;
  status: ConnectorStatus;
}

export interface SegmentStarter {
  /** Stable id used for i18n + spark prefill: `starters.<segmentId>.<id>`. */
  id: string;
}

export interface Segment {
  id: string;
  tier: SegmentTier;
  status: SegmentStatus;
  engineDomain: EngineDomain;
  adapterCrate: string | null;
  Icon: LucideIcon;
  connectors: SegmentConnector[];
  starters: SegmentStarter[];
}

// ─── Helpers ───────────────────────────────────────────────────────

const planned = (...ids: string[]): SegmentConnector[] =>
  ids.map((id) => ({ id, status: 'planned' as const }));

const starters = (...ids: string[]): SegmentStarter[] =>
  ids.map((id) => ({ id }));

// ─── The 26 segments ───────────────────────────────────────────────

export const SEGMENTS: readonly Segment[] = [
  // ── TIER 1 — adapter exists in `crates/gi-adapter-*` ────────────
  {
    id: 'gaming',
    tier: 1,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-gaming',
    Icon: Gamepad2,
    connectors: planned(
      'steam_api',
      'unity_asset_store',
      'unreal_marketplace',
      'discord_bot',
      'twitch_api',
      'epic_games_store',
    ),
    starters: starters(
      'signed_item',
      'achievement_proof',
      'mod_certificate',
      'tournament_result',
      'save_state_proof',
    ),
  },
  {
    id: 'music',
    tier: 1,
    status: 'ready',
    engineDomain: 'Audio',
    adapterCrate: 'gi-adapter-audio',
    Icon: Music,
    connectors: planned(
      'spotify_api',
      'apple_music_api',
      'youtube_music',
      'splice',
      'distrokid',
      'soundcloud',
      'bandcamp',
      'daw_clap_vst3_au',
      'midi_controllers',
    ),
    starters: starters(
      'beat_ownership',
      'stem_pack',
      'song_registration',
      'sample_clearance',
      'collab_split',
    ),
  },
  {
    id: 'video',
    tier: 1,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-video',
    Icon: Video,
    connectors: planned(
      'youtube_api',
      'tiktok_api',
      'vimeo',
      'linkedin_video',
      'runway_api',
      'kling_api',
      'veo_api',
      'frame_io',
      'davinci_resolve',
      'premiere_pro',
    ),
    starters: starters(
      'footage_proof',
      'edit_chain',
      'release_form',
      'content_license',
      'deepfake_shield',
    ),
  },
  {
    id: 'photography',
    tier: 1,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-creative',
    Icon: Camera,
    connectors: planned(
      'adobe_lightroom_api',
      'shutterstock',
      'getty_images',
      'smugmug',
      'fivehundredpx',
      'camera_exif_readers',
    ),
    starters: starters(
      'photo_proof',
      'album_registry',
      'license_manager',
      'print_certificate',
      'watermark_proof',
    ),
  },
  {
    id: 'industry',
    tier: 1,
    status: 'ready',
    engineDomain: 'Process',
    adapterCrate: 'gi-adapter-process',
    Icon: Factory,
    connectors: planned(
      'sap_api',
      'oracle_erp',
      'siemens_mindsphere',
      'schneider_electric',
      'shaam_israel',
      'iot_temperature_sensors',
      'barcode_scanners',
    ),
    starters: starters(
      'quality_check',
      'delivery_proof',
      'temperature_log',
      'batch_certificate',
      'supplier_audit',
    ),
  },
  {
    id: 'healthcare',
    tier: 1,
    status: 'ready',
    engineDomain: 'Medical',
    adapterCrate: 'gi-adapter-medical',
    Icon: Heart,
    connectors: planned(
      'hl7_fhir',
      'epic_api',
      'cerner',
      'clalit_systems',
      'amar_imh',
      'fda_maude',
      'iec_62304_tools',
      'dicom_viewers',
    ),
    starters: starters(
      'incident_report',
      'device_check',
      'patient_consent',
      'shift_handover',
      'lab_result',
    ),
  },
  {
    id: 'finance',
    tier: 1,
    status: 'ready',
    engineDomain: 'Financial',
    adapterCrate: 'gi-adapter-financial',
    Icon: Banknote,
    connectors: planned(
      'stripe_api',
      'paypal',
      'bank_hapoalim_api',
      'leumi_api',
      'quickbooks',
      'xero',
      'bloomberg',
      'pci_dss_scanners',
      'shaam_israel',
    ),
    starters: starters(
      'signed_invoice',
      'audit_trail',
      'transaction_proof',
      'compliance_check',
      'expense_report',
    ),
  },
  {
    id: 'government',
    tier: 1,
    status: 'ready',
    engineDomain: 'Government',
    adapterCrate: 'gi-adapter-government',
    Icon: Landmark,
    connectors: planned(
      'municipal_crm',
      'israel_gov_api',
      'opendata_portals',
      'document_management',
      'citizen_portals',
      'payment_gateways',
    ),
    starters: starters(
      'citizen_request',
      'decision_record',
      'budget_line',
      'permit_issue',
      'foia_response',
    ),
  },
  {
    id: 'legal',
    tier: 1,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-legal',
    Icon: Scale,
    connectors: planned(
      'clio_api',
      'lawnet',
      'court_filing_systems',
      'docusign',
      'evidence_management',
      'body_camera_apis',
      'cctv_systems',
    ),
    starters: starters(
      'evidence_capture',
      'contract_sign',
      'testimony_record',
      'chain_of_custody',
      'nda_proof',
    ),
  },
  {
    id: 'education',
    tier: 1,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-education',
    Icon: GraduationCap,
    connectors: planned(
      'moodle_api',
      'canvas_lms',
      'google_classroom',
      'orcid_api',
      'crossref',
      'university_portals',
      'turnitin',
      'irb_systems',
    ),
    starters: starters(
      'research_claim',
      'grade_certificate',
      'thesis_timestamp',
      'peer_review',
      'credential_proof',
    ),
  },
  {
    id: 'cybersecurity',
    tier: 1,
    status: 'ready',
    engineDomain: 'Defense',
    adapterCrate: 'gi-adapter-cyber',
    Icon: Shield,
    connectors: planned(
      'splunk_api',
      'crowdstrike_falcon',
      'microsoft_sentinel',
      'mitre_attack',
      'virustotal',
      'shodan',
      'siem_systems',
      'jira_incident',
    ),
    starters: starters(
      'threat_alert',
      'incident_chain',
      'forensic_package',
      'vuln_assessment',
      'compliance_audit',
    ),
  },
  {
    id: 'ai',
    tier: 1,
    status: 'coming-soon', // Adapter is AXIOM (separate product); no gi-adapter-ai crate.
    engineDomain: 'Generic',
    adapterCrate: null,
    Icon: BrainCircuit,
    connectors: planned(
      'openai_api',
      'anthropic_api',
      'google_gemini',
      'meta_llama',
      'mistral',
      'groq',
      'deepseek',
      'hugging_face',
      'ollama_local',
    ),
    starters: starters(
      'model_card',
      'training_proof',
      'decision_audit',
      'bias_check',
      'governance_report',
    ),
  },
  {
    id: 'defense',
    tier: 1,
    status: 'ready',
    engineDomain: 'Defense',
    adapterCrate: 'gi-adapter-defense',
    Icon: ShieldHalf,
    connectors: planned(
      'classified_networks',
      'gnss_receivers',
      'tee_hardware_sgx_sev',
      'secure_comms',
      'mil_std_interfaces',
    ),
    starters: starters(
      'classified_doc',
      'field_report',
      'equipment_check',
      'mission_log',
      'intel_package',
    ),
  },

  // ── TIER 2 — adapter planned, segment visible as Coming Soon ────
  {
    id: 'automotive',
    tier: 2,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-automotive',
    Icon: Car,
    connectors: planned(
      'obd_ii_readers',
      'tesla_api',
      'can_bus',
      'ota_update_servers',
      'avas_compliance_tools',
    ),
    starters: starters(
      'firmware_proof',
      'inspection_record',
      'accident_report',
      'avas_compliance',
      'recall_notice',
    ),
  },
  {
    id: 'real-estate',
    tier: 2,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-realestate',
    Icon: Home,
    connectors: planned(
      'tabu_israel',
      'mls_systems',
      'property_management',
      'escrow_services',
      'appraisal_tools',
      'mortgage_systems',
    ),
    starters: starters(
      'deed_proof',
      'appraisal_record',
      'lease_agreement',
      'inspection_report',
      'renovation_permit',
    ),
  },
  {
    id: 'insurance',
    tier: 2,
    status: 'ready',
    engineDomain: 'Financial',
    adapterCrate: 'gi-adapter-insurance',
    Icon: Umbrella,
    connectors: planned(
      'harel_api',
      'menora',
      'clal',
      'migdal',
      'claims_management',
      'actuarial_tools',
      'weather_data_apis',
      'iot_damage_sensors',
    ),
    starters: starters(
      'claim_filing',
      'policy_proof',
      'damage_assessment',
      'underwriting_report',
      'renewal_notice',
    ),
  },
  {
    id: 'logistics',
    tier: 2,
    status: 'ready',
    engineDomain: 'Process',
    adapterCrate: 'gi-adapter-logistics',
    Icon: Truck,
    connectors: planned(
      'dhl_api',
      'fedex',
      'ups',
      'israel_post',
      'asycuda_customs',
      'gps_trackers',
      'wms',
      'barcode_rfid',
    ),
    starters: starters(
      'shipment_proof',
      'customs_declaration',
      'warehouse_receipt',
      'delivery_confirm',
      'return_proof',
    ),
  },
  {
    id: 'hr',
    tier: 2,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-hr',
    Icon: Users,
    connectors: planned(
      'bamboohr',
      'gusto',
      'hibob',
      'linkedin_api',
      'payroll_systems',
      'background_check',
      'training_platforms',
    ),
    starters: starters(
      'offer_letter',
      'employment_verify',
      'training_cert',
      'performance_review',
      'termination_record',
    ),
  },
  {
    id: 'ecommerce',
    tier: 2,
    status: 'ready',
    engineDomain: 'Financial',
    adapterCrate: 'gi-adapter-ecommerce',
    Icon: ShoppingCart,
    connectors: planned(
      'shopify_api',
      'woocommerce',
      'amazon_seller',
      'stripe_api',
      'paypal',
      'shipping_apis',
      'inventory_management',
      'returns_platforms',
    ),
    starters: starters(
      'order_proof',
      'shipping_notice',
      'return_receipt',
      'review_verify',
      'seller_certificate',
    ),
  },
  {
    id: 'construction',
    tier: 2,
    status: 'ready',
    engineDomain: 'Process',
    adapterCrate: 'gi-adapter-construction',
    Icon: HardHat,
    connectors: planned(
      'procore_api',
      'plangrid',
      'bim_360',
      'permit_systems',
      'inspection_apps',
      'safety_management',
    ),
    starters: starters(
      'permit_record',
      'inspection_report',
      'safety_checklist',
      'material_cert',
      'progress_report',
    ),
  },
  {
    id: 'iot',
    tier: 2,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-iot',
    Icon: Cpu,
    connectors: planned(
      'aws_iot_core',
      'azure_iot_hub',
      'google_cloud_iot',
      'mqtt_brokers',
      'sensor_apis',
      'edge_computing',
    ),
    starters: starters(
      'sensor_reading',
      'device_attestation',
      'alert_proof',
      'calibration_cert',
      'data_export',
    ),
  },
  {
    id: 'agriculture',
    tier: 2,
    status: 'ready',
    engineDomain: 'Process',
    adapterCrate: 'gi-adapter-agriculture',
    Icon: Leaf,
    connectors: planned(
      'agritech_platforms',
      'weather_apis',
      'kashrut_authorities',
      'organic_certification',
      'irrigation_systems',
      'drone_imaging',
    ),
    starters: starters(
      'crop_certificate',
      'kashrut_proof',
      'pesticide_log',
      'harvest_record',
      'soil_analysis',
    ),
  },

  // ── TIER 3 — vision; spec lists 3 starters, we pad to 5 placeholders
  {
    id: 'film-production',
    tier: 3,
    status: 'ready',
    engineDomain: 'Generic',
    adapterCrate: 'gi-adapter-film-production',
    Icon: Clapperboard,
    connectors: planned('runway_api', 'kling_api', 'veo_api', 'frame_io'),
    starters: starters(
      'script_analyzer',
      'casting_proof',
      'scene_certificate',
      'soundtrack_credit',
      'final_cut_attestation',
    ),
  },
  {
    id: 'world-building',
    tier: 3,
    status: 'coming-soon',
    engineDomain: 'Generic',
    adapterCrate: null,
    Icon: Globe2,
    connectors: planned('unity_asset_store', 'unreal_marketplace', 'blender_addon'),
    starters: starters(
      'world_blueprint',
      'asset_certificate',
      'terrain_proof',
      'lore_archive',
      'population_attestation',
    ),
  },
  {
    id: 'digital-legacy',
    tier: 3,
    status: 'coming-soon',
    engineDomain: 'Generic',
    adapterCrate: null,
    Icon: Hourglass,
    connectors: planned(
      'will_registries',
      'password_managers',
      'cloud_storage_apis',
      'social_media_apis',
      'estate_lawyers',
    ),
    starters: starters(
      'last_will',
      'digital_inheritance',
      'memory_archive',
      'account_handover',
      'final_message',
    ),
  },
  {
    id: 'autonomous-business',
    tier: 3,
    status: 'coming-soon',
    engineDomain: 'Generic',
    adapterCrate: null,
    Icon: Bot,
    connectors: planned(
      'llm_apis',
      'banking_apis',
      'payment_processors',
      'multi_agent_frameworks',
      'compliance_oracles',
    ),
    starters: starters(
      'agent_charter',
      'decision_log',
      'revenue_split',
      'governance_proof',
      'shutdown_clause',
    ),
  },
];

// Compile-time and at-import-time invariants — fail loudly during dev,
// not at user click time.
if (SEGMENTS.length !== 26) {
  throw new Error(`segments.ts must declare exactly 26 segments; got ${SEGMENTS.length}`);
}
const _ids = new Set<string>();
for (const s of SEGMENTS) {
  if (_ids.has(s.id)) {
    throw new Error(`segments.ts: duplicate segment id "${s.id}"`);
  }
  _ids.add(s.id);
  if (s.starters.length !== 5) {
    throw new Error(`segments.ts: segment "${s.id}" has ${s.starters.length} starters; expected 5`);
  }
  if (s.status === 'ready' && !s.adapterCrate) {
    throw new Error(`segments.ts: segment "${s.id}" is "ready" but has no adapterCrate`);
  }
}

// ─── Lookups ───────────────────────────────────────────────────────

export const SEGMENT_IDS: readonly string[] = SEGMENTS.map((s) => s.id);

const _byId = new Map(SEGMENTS.map((s) => [s.id, s]));
export function getSegment(id: string): Segment | undefined {
  return _byId.get(id);
}

/** Find the segment that owns a given starter id. Used by Spark when
 *  invoked with `?starter=<id>` to back-reference the segment + its
 *  recommended `engineDomain`. Returns `undefined` if no segment owns
 *  a starter with that id. */
export function getSegmentForStarter(
  starterId: string,
): { segment: Segment; starter: SegmentStarter } | undefined {
  for (const segment of SEGMENTS) {
    const starter = segment.starters.find((s) => s.id === starterId);
    if (starter) return { segment, starter };
  }
  return undefined;
}

/** Tier 1 segments only — used by the Spark domain selector to surface
 *  the most-actionable subset first when the user has not clicked any
 *  segment-specific starter. */
export const TIER_1_SEGMENTS: readonly Segment[] = SEGMENTS.filter((s) => s.tier === 1);
